import {
  Alert,
  Box,
  Container,
  Flex,
  Progress,
  Stack,
  Text,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "@tanstack/react-query"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { FiCheck, FiLock, FiX } from "react-icons/fi"
import { z } from "zod"

import { Button } from "@/components/atoms/Button"
import { Field } from "@/components/atoms/Field"
import { PasswordInput } from "@/components/atoms/PasswordInput"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"

const passwordSchema = z
  .object({
    current_password: z.string().min(1, "Current password is required"),
    new_password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(
        /[^A-Za-z0-9]/,
        "Password must contain at least one special character",
      ),
    confirm_password: z.string(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ["confirm_password"],
  })

type PasswordFormData = z.infer<typeof passwordSchema>

export interface PasswordChangeFormProps {
  onSuccess?: () => void
}

const getPasswordStrength = (
  password: string,
): {
  score: number
  label: string
  color: string
  checks: Array<{ label: string; passed: boolean }>
} => {
  const checks = [
    { label: "At least 8 characters", passed: password.length >= 8 },
    { label: "Contains uppercase letter", passed: /[A-Z]/.test(password) },
    { label: "Contains lowercase letter", passed: /[a-z]/.test(password) },
    { label: "Contains number", passed: /[0-9]/.test(password) },
    {
      label: "Contains special character",
      passed: /[^A-Za-z0-9]/.test(password),
    },
  ]

  const score = checks.filter((check) => check.passed).length

  let label = "Very Weak"
  let color = "red"

  if (score >= 5) {
    label = "Very Strong"
    color = "green"
  } else if (score >= 4) {
    label = "Strong"
    color = "blue"
  } else if (score >= 3) {
    label = "Medium"
    color = "yellow"
  } else if (score >= 2) {
    label = "Weak"
    color = "orange"
  }

  return { score: (score / 5) * 100, label, color, checks }
}

export const PasswordChangeForm = ({ onSuccess }: PasswordChangeFormProps) => {
  const [showStrength, setShowStrength] = useState(false)
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  })

  const newPassword = watch("new_password", "")
  const passwordStrength = getPasswordStrength(newPassword || "")

  const changePasswordMutation = useMutation({
    mutationFn: async (data: PasswordFormData) => {
      const response =
        await api.users.changePasswordApiV1UsersMeChangePasswordPost({
          requestBody: {
            current_password: data.current_password,
            new_password: data.new_password,
          },
        })
      return response.data
    },
    onSuccess: () => {
      showSuccessToast("Password changed successfully")
      reset()
      setShowStrength(false)
      onSuccess?.()
    },
    onError: (error: Error) => {
      showErrorToast(
        `Failed to change password: ${error?.message || "Unknown error"}`,
      )
    },
  })

  const onSubmit = (data: PasswordFormData) => {
    changePasswordMutation.mutate(data)
  }

  return (
    <Container maxW="md" p={0}>
      <Stack gap={6}>
        {/* Header */}
        <Stack gap={2}>
          <Text fontSize="lg" fontWeight="semibold">
            Change Password
          </Text>
          <Text fontSize="sm" color="gray.600">
            Ensure your account is using a long, random password to stay secure.
          </Text>
        </Stack>

        {/* Form */}
        <Box as="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={4}>
            {/* Current Password */}
            <Field
              label="Current Password"
              required
              invalid={!!errors.current_password}
              errorText={errors.current_password?.message}
            >
              <PasswordInput
                type="current_password"
                {...register("current_password")}
                placeholder="Enter your current password"
              />
            </Field>

            {/* New Password */}
            <Field
              label="New Password"
              required
              invalid={!!errors.new_password}
              errorText={errors.new_password?.message}
            >
              <PasswordInput
                type="new_password"
                {...register("new_password")}
                placeholder="Enter your new password"
                onFocus={() => setShowStrength(true)}
              />
            </Field>

            {/* Password Strength Indicator */}
            {showStrength && newPassword && (
              <Stack gap={3} p={3} bg="gray.50" borderRadius="md">
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" fontWeight="medium">
                    Password Strength
                  </Text>
                  <Text fontSize="sm" color={`${passwordStrength.color}.500`}>
                    {passwordStrength.label}
                  </Text>
                </Flex>
                <Progress.Root
                  value={passwordStrength.score}
                  max={100}
                  size="sm"
                >
                  <Progress.Track>
                    <Progress.Range colorPalette={passwordStrength.color} />
                  </Progress.Track>
                </Progress.Root>
                <Stack gap={1}>
                  {passwordStrength.checks.map((check, index) => (
                    <Flex key={index} align="center" gap={2}>
                      {check.passed ? (
                        <FiCheck size={14} color="green" />
                      ) : (
                        <FiX size={14} color="red" />
                      )}
                      <Text
                        fontSize="xs"
                        color={check.passed ? "green.600" : "gray.500"}
                      >
                        {check.label}
                      </Text>
                    </Flex>
                  ))}
                </Stack>
              </Stack>
            )}

            {/* Confirm Password */}
            <Field
              label="Confirm New Password"
              required
              invalid={!!errors.confirm_password}
              errorText={errors.confirm_password?.message}
            >
              <PasswordInput
                type="confirm_password"
                {...register("confirm_password")}
                placeholder="Confirm your new password"
              />
            </Field>

            {/* Security Notice */}
            <Alert.Root status="info" size="sm">
              <Alert.Indicator />
              <Alert.Description>
                After changing your password, you'll remain logged in on this
                device, but may be logged out of other devices for security.
              </Alert.Description>
            </Alert.Root>

            {/* Action Buttons */}
            <Flex gap={3} justify="end">
              <Button
                variant="outline"
                onClick={() => {
                  reset()
                  setShowStrength(false)
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting} colorScheme="blue">
                <FiLock />
                Change Password
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Stack>
    </Container>
  )
}

export default PasswordChangeForm
